/**
 * ExclusiveContentSubscriptionActionsService
 *
 * Domain service grouping the action endpoints for exclusive-content
 * subscriptions (subscribe / unsubscribe, request / cancel-request,
 * creator-side review of a request (approve / reject), approval-required
 * check). Mirrors Flutter's SubscriptionsRepository action methods.
 *
 * Distinct from `SubscriptionService` which handles pagination/listing.
 *
 * Architecture:
 * UseCase → ExclusiveContentSubscriptionActionsService → Data layer
 */

import type { Unsubscribe } from 'firebase/firestore';

import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { firestoreService } from '@/data/services/firestoreService';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { ExclusiveContentSubscriptionRemapper } from '@/domain/remappers/exclusiveContentSubscription/exclusiveContentSubscriptionRemapper';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class ExclusiveContentSubscriptionActionsService {
  private static instance: ExclusiveContentSubscriptionActionsService;

  private constructor() {}

  static getInstance(): ExclusiveContentSubscriptionActionsService {
    if (!this.instance) {
      this.instance = new ExclusiveContentSubscriptionActionsService();
    }
    return this.instance;
  }

  /**
   * Subscribe or unsubscribe to a creator's exclusive content.
   * Delegates to the cloud function `subscribeToUser`.
   */
  async subscribe(userId: string, subscribe: boolean): Promise<boolean> {
    return cloudFunctionsService.subscribeToUser(userId, subscribe);
  }

  /**
   * Request or cancel a subscription request for an approval-required creator.
   * Delegates to the cloud function `requestExclusiveSubscription`.
   */
  async requestSubscription(userId: string, requested: boolean): Promise<boolean> {
    return cloudFunctionsService.requestExclusiveSubscription(userId, requested);
  }

  /**
   * Review (confirm or reject) a pending exclusive-subscription request as the
   * creator. Delegates to the cloud function `reviewExclusiveSubscription`.
   *
   * A pure pass-through, mirroring Flutter's
   * `SubscriptionsRepository.reviewExclusiveSubscription`
   * (lib/data/repository/subscriptions_repository.dart:73-84): no branching, no
   * guards, no local writes.
   *
   * The reviewer (creator) is never sent: the cloud function resolves it from
   * `auth.uid` (review_exclusive_subscription.ts:18) behind an `unauthenticated`
   * gate (:10-15). The cloud function also owns every side-effect — the outcome
   * notifications to both users, `actioned: true` on the original request
   * notification, the subscription deletes on reject, and
   * `handleUserSubscription` (coins/subscription) on accept — so this layer
   * reimplements none of it and writes nothing itself.
   *
   * @param requestNotificationId - Id of the original request notification
   * @param userId - Id of the requesting (peer) user, not the reviewer
   * @param accept - True to confirm the request, false to reject it
   */
  async reviewSubscription(
    requestNotificationId: string,
    userId: string,
    accept: boolean,
  ): Promise<boolean> {
    return cloudFunctionsService.reviewExclusiveSubscription(requestNotificationId, userId, accept);
  }

  /**
   * Check if the target creator requires manual approval for new subscribers.
   * Reads `users/{targetUserId}/userSettings/approve_subscribers` and returns
   * `true` only when the document exists with `value === 1`.
   */
  async shouldSendSubscriptionRequest(targetUserId: string): Promise<boolean> {
    return firestoreService.shouldSendSubscriptionRequest(targetUserId);
  }

  /**
   * Listen to live Firestore changes of the current user's exclusive-content
   * subscriptions and mirror each change into SecureStorage.
   *
   * Mirrors Flutter's ExclusiveContentService.listenToExclusiveContentChanges
   * (lib/services/exclusive_content_service.dart:13-62):
   *   - added/modified with status == inactive  → remove from local store
   *   - added/modified with any other status    → put into local store
   *   - removed                                  → remove from local store
   *
   * Per-change errors are logged via appLogger.error and swallowed so a single
   * bad doc cannot break the listener. Returns the underlying Firestore
   * Unsubscribe; the caller is responsible for cleanup.
   */
  listenToExclusiveContentChanges(currentUserFirebaseId: string): Unsubscribe {
    const secureStorage = SecureStorageWebCrypto.getInstance();

    return firestoreService.subscribeToExclusiveContentSubscriptionsLiveChanges(
      currentUserFirebaseId,
      async changes => {
        for (const change of changes) {
          const dto = ExclusiveContentSubscriptionRemapper.fromJson(change.data);
          const key = SECURE_STORAGE_KEYS.EXCLUSIVE_SUBSCRIPTIONS + dto.userId;

          try {
            if (change.type === 'removed') {
              await secureStorage.remove(key);
            } else if (dto.status === ExclusiveContentSubscriptionStatus.inactive) {
              await secureStorage.remove(key);
            } else {
              const storage = ExclusiveContentSubscriptionRemapper.toStorage(dto);
              await secureStorage.setEncrypted(key, storage);
            }
          } catch (error) {
            appLogger.error(
              'Error syncing exclusive content subscription change',
              'ExclusiveContentSubscriptionActionsService', { context: { type: change.type, userId: dto.userId }, error: error as Error },
            );
          }
        }
      },
      error => {
        appLogger.error(
          'Error in exclusive content subscriptions snapshot listener',
          'ExclusiveContentSubscriptionActionsService', { error: error },
        );
      },
    );
  }
}

// Export singleton instance for convenience
export const exclusiveContentSubscriptionActionsService =
  ExclusiveContentSubscriptionActionsService.getInstance();
